import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate } from '~/types/dates'
import {
  type AttachmentId,
  newId,
  type SentinelLiftId,
  type StrengthEntryId,
  type WeeklyReviewId,
} from '~/types/ids'
import type { Kilograms } from '~/types/units'
import { toKilograms, unwrapNumber } from '~/types/units'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * One recorded set of a sentinel lift (data-model.md).
 *
 * **Raw observations only.** No estimated 1RM is stored here. D-069 approves
 * Epley at `estimated_1rm_epley_v1` with sets above ten reps excluded, and the
 * calculation lives in `calculations/estimated1RM.ts` (Checkpoint 2.2) where it
 * runs on READ. Deriving it at write time would freeze today's formula into
 * records that outlive it, which is exactly what data-model.md's "keep raw
 * observations separate from derived values" forbids — and it is what lets a
 * future `_v2` reinterpret the same history.
 *
 * A set above the rep cap is **stored like any other** and excluded from the
 * estimate later, with a reason. Refusing it here would lose real training data
 * to a display rule. The set is named in the interface and excluded with its
 * reason rather than silently dropped.
 */
export interface StrengthEntryRecord extends RecordEnvelope {
  readonly id: StrengthEntryId
  readonly liftId: SentinelLiftId
  readonly date: CalendarDate
  readonly loadKg: Kilograms
  readonly reps: number
  readonly rpe?: number
  readonly repsInReserve?: number
  /** Body weight at the time, for the relative-strength comparison (D-069). */
  readonly bodyWeightKg?: Kilograms
  readonly attachmentId?: AttachmentId
  readonly reviewId?: WeeklyReviewId
  readonly notes?: string
}

export interface StrengthEntryInput {
  readonly liftId: SentinelLiftId
  readonly date: string
  readonly loadKg: number
  readonly reps: number
  readonly rpe?: number
  readonly repsInReserve?: number
  readonly bodyWeightKg?: number
  readonly attachmentId?: AttachmentId
  readonly reviewId?: WeeklyReviewId
  readonly notes?: string
}

/* Bounds catch transposed digits and unit mistakes, not unusual lifters. A bar
   loaded past 600 kg or a set of more than 100 reps is an entry error. */
const MAX_LOAD_KG = 600
const MAX_REPS = 100
const RPE_MIN = 1
const RPE_MAX = 10
const MAX_REPS_IN_RESERVE = 10

export function validateStrengthEntry(input: StrengthEntryInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (toCalendarDate(input.date) === undefined) {
    issues.push(issue('date', 'Date must be a real date in YYYY-MM-DD form.'))
  }

  if (String(input.liftId).trim().length === 0) {
    issues.push(issue('liftId', 'A set must belong to a lift.'))
  }

  const load = toKilograms(input.loadKg)
  if (load === undefined) {
    issues.push(issue('loadKg', 'Load must be a positive number in kilograms.'))
  } else if (unwrapNumber(load) > MAX_LOAD_KG) {
    issues.push(issue('loadKg', `Load must be ${MAX_LOAD_KG} kg or less.`))
  }

  if (!Number.isInteger(input.reps) || input.reps < 1 || input.reps > MAX_REPS) {
    issues.push(issue('reps', `Reps must be a whole number from 1 to ${MAX_REPS}.`))
  }

  if (input.rpe !== undefined && (input.rpe < RPE_MIN || input.rpe > RPE_MAX)) {
    issues.push(issue('rpe', `RPE runs from ${RPE_MIN} to ${RPE_MAX}.`))
  }

  if (
    input.repsInReserve !== undefined &&
    (!Number.isInteger(input.repsInReserve) ||
      input.repsInReserve < 0 ||
      input.repsInReserve > MAX_REPS_IN_RESERVE)
  ) {
    issues.push(
      issue(
        'repsInReserve',
        `Reps in reserve must be a whole number from 0 to ${MAX_REPS_IN_RESERVE}.`,
      ),
    )
  }

  if (input.bodyWeightKg !== undefined && toKilograms(input.bodyWeightKg) === undefined) {
    issues.push(issue('bodyWeightKg', 'Body weight must be a positive number in kilograms.'))
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

/** Identity carried across a correction, so an edit does not mint a new set. */
export interface ExistingStrengthEntryIdentity {
  readonly id: StrengthEntryId
  readonly createdAt: IsoTimestamp
}

export function buildStrengthEntryRecord(
  input: StrengthEntryInput,
  now: IsoTimestamp,
  existing?: ExistingStrengthEntryIdentity,
): Result<StrengthEntryRecord, ValidationIssues> {
  const issues = validateStrengthEntry(input)
  if (issues.length > 0) return err(issues)

  const date = toCalendarDate(input.date)
  const loadKg = toKilograms(input.loadKg)
  if (date === undefined || loadKg === undefined) {
    return err([issue('strengthEntry', 'Strength entry failed construction.')])
  }

  const bodyWeightKg =
    input.bodyWeightKg === undefined ? undefined : toKilograms(input.bodyWeightKg)

  return ok({
    id: existing?.id ?? newId<'StrengthEntryId'>(),
    liftId: input.liftId,
    date,
    loadKg,
    reps: input.reps,
    ...(input.rpe === undefined ? {} : { rpe: input.rpe }),
    ...(input.repsInReserve === undefined ? {} : { repsInReserve: input.repsInReserve }),
    ...(bodyWeightKg === undefined ? {} : { bodyWeightKg }),
    ...(input.attachmentId === undefined ? {} : { attachmentId: input.attachmentId }),
    ...(input.reviewId === undefined ? {} : { reviewId: input.reviewId }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}
